/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { TerminalPlatformsEnum } from '../../shared/entities/terminalPlatform';
import AircraftTailTag from './AircraftTailTag';
import { TerminalIconStaticMarker } from './TerminalIconStaticMarker';
import { TerminalIconStaticMarkerStylesForMap } from './TerminalIconStaticMarkerStyles';
import Terminal, { NetworkGroup } from '../../shared/entities/terminal';
import { IDToken } from '@okta/okta-auth-js';

interface TerminalIconContainerProps {
  otherTerminalsOnAircraft: Terminal[] | null;
  platformType: TerminalPlatformsEnum;
  connectivityStatus: string;
  datapathUuid: string;
  userData: IDToken | undefined;
  networkGroup: NetworkGroup;
  appConfig?: any;
}

/**
 * This class represents the container for both the aircraft icon and the tail tag.
 * The class will handle the positioning of the tail in reference to the aircraft icon.
 */
const TerminalIconContainer = (props: TerminalIconContainerProps): React.ReactComponentElement<'div', {}> => {
  const markerDiv = (
    <TerminalIconStaticMarker
      datapathUuid={props.datapathUuid}
      platformType={props.platformType}
      connectivityStatus={props.connectivityStatus}
      markerStyles={TerminalIconStaticMarkerStylesForMap}
      appConfig={props.appConfig}
    />
  );

  return (
    <div>
      {markerDiv}
      <div className="pulse" />
      <div className="map__tailtag--container">
        <div className="map__tailtag--header">
          <AircraftTailTagHeader datapathUuid={props.datapathUuid} />
        </div>
        <hr className="map__tailtag--divider" />
        <AircraftTailTag
          key={0}
          terminalNum={0}
          datapathUuid={props.datapathUuid}
          userData={props.userData}
          networkGroup={props.networkGroup}
          platformType={props.platformType}
        />
        {props.otherTerminalsOnAircraft
          ? props.otherTerminalsOnAircraft.map((terminal, index) => (
              <React.Fragment key={index + 1}>
                <hr className="map__tailtag--divider" />
                <AircraftTailTag
                  terminalNum={index + 1}
                  datapathUuid={terminal.datapathUuid}
                  userData={props.userData}
                  networkGroup={terminal.networkGroup}
                  platformType={terminal.platformType}
                />
              </React.Fragment>
            )) // Add additional tail tags 1...n
          : null}
      </div>
    </div>
  );
};

interface AircraftTailTagHeaderProps {
  datapathUuid: string;
}

const AircraftTailTagHeader = ({ datapathUuid }: AircraftTailTagHeaderProps) => {
  return (
    <>
      <div data-test={`map__tailtag__${datapathUuid}--tailname`} className={`map__tailtag--tailname`} />
      <div data-test={`map__tailtag__${datapathUuid}--altitudecontainer`} className={`map__tailtag--altitudecontainer`}>
        <svg viewBox="0 0 24 24">
          <path
            data-test={`map__tailtag__${datapathUuid}--takeofficon`}
            className={`map__tailtag--takeofficon`}
            d="M2.5 19h19v2h-19zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 1.82 3.16.77 1.33 1.6-.43 5.31-1.42 4.35-1.16L21 11.49c.81-.23 1.28-1.05 1.07-1.85z"
          />
          <path
            data-test={`map__tailtag__${datapathUuid}--landingicon`}
            className={`map__tailtag--landingicon`}
            d="M2.5 19h19v2h-19zm7.18-5.73l4.35 1.16 5.31 1.42c.8.21 1.62-.26 1.84-1.06.21-.8-.26-1.62-1.06-1.84l-5.31-1.42-2.76-9.02L10.12 2v8.28L5.15 8.95l-.93-2.32-1.45-.39v5.17l1.6.43 5.31 1.43z"
          />
          <path
            data-test={`map__tailtag__${datapathUuid}--inflighticon`}
            className="map__tailtag--inflighticon"
            d="M7.9749,15.5 C10.3229,15.5 12.3509,16.851 13.3349,18.816 C14.2929,18.002 15.5249,17.5 16.8809,17.5 C19.2119,17.5 21.1989,18.953 21.9999,21 L21.9999,21 L19.7449,21 C19.1119,20.096 18.0659,19.5 16.8809,19.5 C15.6949,19.5 14.6499,20.096 14.0159,21 L14.0159,21 L11.9239,21 C11.6749,19.032 10.0089,17.5 7.9749,17.5 C5.9409,17.5 4.2749,19.032 4.0259,21 L4.0259,21 L1.9999,21 C2.2549,17.921 4.8299,15.5 7.9749,15.5 Z M10.0006,1.9995 L15.0006,9.9985 L20.4976,9.9955 C21.3276,10.0005 21.9936,10.6715 21.9976,11.4985 C21.9926,12.3285 21.3216,12.9945 20.4946,12.9985 L20.4946,12.9985 L3.3406,12.9985 L2.0026,8.0045 L3.5036,8.0065 L5.0026,10.0065 L10.1476,10.0045 L8.0046,2.0065 L10.0006,1.9995 Z"
          />
          <path
            data-test={`map__tailtag__${datapathUuid}--ondeckicon`}
            className="map__tailtag--ondeckicon"
            d="M21.5,19 L21.5,21 L2.5,21 L2.5,19 L21.5,19 Z M12.5,14.5 C13.329,14.5 14,15.171 14,16 C14,16.829 13.329,17.5 12.5,17.5 C11.671,17.5 11,16.829 11,16 C11,15.171 11.671,14.5 12.5,14.5 Z M17.5,14.5 C18.329,14.5 19,15.171 19,16 C19,16.829 18.329,17.5 17.5,17.5 C16.671,17.5 16,16.829 16,16 C16,15.171 16.671,14.5 17.5,14.5 Z M10.0006,1.9995 L15.0006,9.9985 L20.4976,9.9955 C21.3276,10.0005 21.9936,10.6715 21.9976,11.4985 C21.9926,12.3285 21.3216,12.9945 20.4946,12.9985 L20.4946,12.9985 L3.3406,12.9985 L2.0026,8.0045 L3.5036,8.0065 L5.0026,10.0065 L10.1476,10.0045 L8.0046,2.0065 L10.0006,1.9995 Z"
          />
        </svg>
        <div data-test={`map__tailtag__${datapathUuid}--altitude`} className={`map__tailtag--altitude`} />
      </div>
    </>
  );
};

export default TerminalIconContainer;
